import {
  RequestHeaders,
  APIFetchStudio,
  APIFetchStudioActivityLogs,
  APIFetchStudioV2,
  APIFetchV3
} from "@certa/network";
import type { PaginatedResultsResponse } from "../types/generic.types";
import type {
  AddStudioProcessToBranchParams,
  CreateBranchSavepointResponse,
  CreateStudioAppParams,
  UpdateStudioAppParams,
  CreateStudioProcessParams,
  UpdateStudioProcessParams,
  UpdateStudioProcessResponse,
  GetStudioProcessDeployableJSONParams,
  GetStudioProcessDeployableJSONResponse,
  GetStudioProcessMetaJSONBySavepointParams,
  GetStudioProcessMetaJSONResponse,
  ProcessDeploymentLogResponse,
  SavepointDeploymentLogResponse,
  StudioAppResponse,
  StudioBranch,
  StudioBranchResponse,
  StudioBranchSavePoint,
  StudioBranchSavepointPayload,
  StudioBranchSavePointResponse,
  StudioPermissionsResponse,
  StudioProcessBySavePointResponse,
  StudioProcessResponse,
  UpdateDeployableJSONParams,
  UpdateStudioBranchSavepointResponse,
  UpdateStudioProcessMetaJSONParams,
  GetWFUpdateInProgressResponse,
  DeleteStudioAppParams,
  ECARunResponse,
  UpdateMegaJSONParams,
  UpdateMegaJSONResponse,
  CreateMegaJSONResponse,
  GetMegaJSONResponse
} from "../types/studio.types";
import {
  studioAppsModelCreator,
  studioAppModelCreator,
  studioProcessModelCreator,
  studioProcessesModelCreator,
  studioBranchSavePointsModelCreator,
  studioProcessesBySavePointsModelCreator,
  savepointDeploymentLogsModelCreator,
  processDeploymentLogsModelCreator,
  studioPermissionsModelCreator,
  studioBranchesModelCreator,
  studioBranchSavePointModelCreator
} from "../models/studio.models";
import type { HashMap } from "@certa/types";
import type { ActivityLogData, Process } from "@certa/studio-core";
import type { UpdateMetaJSONAndLogRequestData } from "@certa/studio-activity-logs/";
import type { ZodRawProcess } from "../schemas/studio/deployableJSON.schema";

/**
 * Creates a new studio app that works as a logical encapsulating unit
 * for processes. Before we trigger this service, we need to make sure
 * that we also have a workflow created for the App, which will be bound
 * to the newly created app, for handling Approval flow process.
 * @param payload
 * @returns
 */
export function createStudioApp(payload: CreateStudioAppParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio<StudioAppResponse>("package/", requestOptions);
}

export function updateStudioApp(params: UpdateStudioAppParams) {
  const { id: packageId, ...payload } = params;
  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio<StudioAppResponse>(
    `package/${packageId}/`,
    requestOptions
  );
}

export function deleteStudioApp(params: DeleteStudioAppParams) {
  const { id: packageId } = params;
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio(`package/${packageId}/`, requestOptions);
}

/**
 * Returns the list of all the apps that are currently created on the
 * current instance.
 * @returns
 */
export function getStudioApps(offset = 0) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const url = `package/?limit=1000&offset=${offset}`;
  return APIFetchStudio<PaginatedResultsResponse<StudioAppResponse>>(
    url,
    requestOptions
  )
    .then(studioAppsModelCreator)
    .catch(error => {
      if (
        error instanceof Response &&
        (error.status === 401 || error.status === 403)
      ) {
        throw new Error(
          `Studio access denied (${error.status}): Please check your permissions`
        );
      }
      throw error;
    });
}

/**
 * Returns the details of the specific app.
 * @param appId
 * @returns
 */
export function getStudioApp(appId: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<StudioAppResponse>(`package/${appId}/`, requestOptions)
    .then(studioAppModelCreator)
    .catch(error => {
      if (
        error instanceof Response &&
        (error.status === 401 || error.status === 403)
      ) {
        throw new Error(
          `Studio app access denied (${error.status}): Please check your permissions for app ${appId}`
        );
      }
      throw error;
    });
}

/**
 * Creates a process within an app.
 * @param payload
 * @returns
 */
export function createStudioAppProcess(payload: CreateStudioProcessParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio<StudioProcessResponse>("process/", requestOptions);
}

export function updateStudioAppProcess(params: UpdateStudioProcessParams) {
  const { processId, ...payload } = params;

  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio<UpdateStudioProcessResponse>(
    `process/${processId}/`,
    requestOptions
  );
}

export function getStudioProcesses(packageId: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<PaginatedResultsResponse<StudioProcessResponse>>(
    `process/?packages=${packageId}`,
    requestOptions
  ).then(studioProcessesModelCreator);
}

export function getStudioProcess(packageId: number, processId: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<StudioProcessResponse>(
    `process/${processId}/?packages=${packageId}`,
    requestOptions
  ).then(studioProcessModelCreator);
}

// Branch

export function getStudioBranches(packageId: number, pageParam: number = 0) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<PaginatedResultsResponse<StudioBranchResponse>>(
    `branch/?package=${packageId}&offset=${pageParam || "0"}&limit=500`,
    requestOptions
  ).then(studioBranchesModelCreator);
}

export function createBranch(payload: {
  sourceBranchSavepointId: number;
  branchName: string;
  branchSavepointTitle: string;
  branchSavepointDescription?: string;
}) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      source_branch_savepoint_id: payload.sourceBranchSavepointId,
      name: payload.branchName,
      branch_savepoint_title: payload.branchSavepointTitle,
      branch_savepoint_description: payload.branchSavepointDescription
    })
  };

  return APIFetchStudio<StudioBranch>(`branch/`, requestOptions);
}

export function mergeBranchesV2(payload: {
  sourceBranchSavepointId: number;
  targetBranchId: number;
  branchSavepointTitle: string;
  branchSavepointDescription?: string;
  processHashMap: Record<string, Process>;
}) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      source_branch_savepoint_id: payload.sourceBranchSavepointId,
      target_branch_id: payload.targetBranchId,
      branch_savepoint_title: payload.branchSavepointTitle,
      branch_savepoint_description: payload.branchSavepointDescription,
      process_kind_meta_json_map: payload.processHashMap
    })
  };

  return APIFetchStudioV2(`branch/merge/`, requestOptions);
}

export function getStudioBranchSavePoints(
  branchId: number,
  pageParam?: number
) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<
    PaginatedResultsResponse<StudioBranchSavePointResponse>
  >(
    `branch-savepoint/?branch=${branchId}&offset=${pageParam || "0"}&limit=500`,
    requestOptions
  ).then(studioBranchSavePointsModelCreator);
}

export function getStudioBranchSavePoint(branchSavepointId: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<StudioBranchSavePointResponse>(
    `branch-savepoint/${branchSavepointId}/`,
    requestOptions
  ).then(studioBranchSavePointModelCreator);
}

export function getStudioProcessesBySavePoint(savepointId: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  /**
   * NOTE: Hardcoded the limit here because it's highly unlikely
   * that an app would have more than 500 processes.
   *
   * This can be properly handled by paginated query if needed.
   * But if we do that, we need to see how we can make the
   * Mutation query work, which calls this same service,
   * but just on-demand.
   */
  return APIFetchStudio<
    PaginatedResultsResponse<StudioProcessBySavePointResponse>
  >(
    `process-savepoint/?branch_savepoints=${savepointId}&limit=500&offset=0`,
    requestOptions
  ).then(studioProcessesBySavePointsModelCreator);
}

export function addStudioProcessToBranch(
  payload: AddStudioProcessToBranchParams
) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio(
    `branch/${payload.branch_id}/add-process/`,
    requestOptions
  );
}

export function updateDeployableJSON(payload: UpdateDeployableJSONParams) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  // On Success, returns { "deployable_json_doc_id": string }, that we don't use.
  return APIFetchStudio(`update-deployable-json/`, requestOptions);
}

export function getDeployableJSON(
  payload: GetStudioProcessDeployableJSONParams
) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  // On Success, returns { "deployable_json_doc_id": string }, that we don't use.
  return APIFetchStudio<GetStudioProcessDeployableJSONResponse>(
    `get-deployable-json/`,
    requestOptions
  ).then(response => response.deployable_json);
}

// CRUD on Process

export function getStudioProcessMetaJSONBySavepoint(
  payload: GetStudioProcessMetaJSONBySavepointParams
) {
  // TODO: params should be in camelCase, we should convert it to snake_case in payload
  const { process_savepoint_id: processSavepointId, ...requestPayload } =
    payload;
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(requestPayload)
  };

  return APIFetchStudio<GetStudioProcessMetaJSONResponse>(
    `process-savepoint/${processSavepointId}/get-meta-json/`,
    requestOptions
  ).then(response => response.meta_json);
}

export function updateStudioProcessMetaJSON(
  payload: UpdateStudioProcessMetaJSONParams
) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio(`update-meta-json/`, requestOptions);
}

export function startWorkingOnBranch(branchId: number) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio(
    `branch/${branchId}/set-stage/start-work/`,
    requestOptions
  );
}

export function stopWorkingOnBranch(branchId: number) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio(
    `branch/${branchId}/set-stage/stop-work/`,
    requestOptions
  );
}

export function lockBranch(branchId: number) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio(`branch/${branchId}/set-stage/lock/`, requestOptions);
}

export function unlockBranch(branchId: number) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio(`branch/${branchId}/set-stage/unlock/`, requestOptions);
}

/**
 * This is not supposed to be triggered from FE, but in case
 * we ever need it (for testing), it's here. So no need to find that API DOC
 */
export function validateBranch(props: {
  branchId: number;
  targetEnv: string;
  updateInProgressWorkflows: boolean;
}) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      target_env: props.targetEnv,
      update_inprogress_workflows: props.updateInProgressWorkflows
    })
  };

  return APIFetchStudio(
    `branch/${props.branchId}/set-stage/validate/`,
    requestOptions
  );
}

// Deployment Logs

export function getSavepointDeploymentLogs(savepointId: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<
    PaginatedResultsResponse<SavepointDeploymentLogResponse>
  >(`branch-task-log/?branch_savepoint=${savepointId}`, requestOptions).then(
    savepointDeploymentLogsModelCreator
  );
}

export function getProcessDeploymentLogs(branchLogId: string) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<PaginatedResultsResponse<ProcessDeploymentLogResponse>>(
    `process-task-log/?branch_log=${branchLogId}&offset=0&limit=500`,
    requestOptions
  ).then(processDeploymentLogsModelCreator);
}

export function updateBranchSavepoint(params: StudioBranchSavepointPayload) {
  const { savepointId, ...payload } = params;

  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio<UpdateStudioBranchSavepointResponse>(
    `branch-savepoint/${savepointId}/`,
    requestOptions
  );
}
// Permissions

export function getStudioPermissions() {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchStudio<StudioPermissionsResponse>(
    `my-permissions/`,
    requestOptions
  )
    .then(studioPermissionsModelCreator)
    .catch(error => {
      if (
        error instanceof Response &&
        (error.status === 401 || error.status === 403)
      ) {
        throw new Error(
          `Studio permissions check failed (${error.status}): Authentication required`
        );
      }
      throw error;
    });
}

export function getUpdateInProgressWF(targetEnv: string) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      target_env: targetEnv
    })
  };
  return APIFetchStudio<GetWFUpdateInProgressResponse>(
    `get-restricted-update-in-progress-value/`,
    requestOptions
  ).then(res => res.enable_update_in_progress);
}

// Create savepoint after deployment

export function createBranchSavepoint({
  branchId,
  branchSavepointTitle,
  branchSavepointDescription
}: {
  branchId: number;
  branchSavepointTitle: string;
  branchSavepointDescription: string;
}) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      branch_id: branchId,
      title: branchSavepointTitle,
      description: branchSavepointDescription
    })
  };

  return APIFetchStudio<CreateBranchSavepointResponse>(
    `branch-savepoint/`,
    requestOptions
  );
}

export function uploadStudioFilesToS3(payload: {
  branchSavepoint: string;
  processSavpoint: string;
  file: Blob;
}) {
  const formData = new FormData();

  formData.append("process_savepoint", payload.processSavpoint);
  formData.append("branch_savepoint", payload.branchSavepoint);
  formData.append("file", payload.file);

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: formData
  };

  delete (requestOptions.headers as HashMap)["Content-Type"];

  return APIFetchStudio<{ path: string }>(
    `upload-process-config-file/`,
    requestOptions
  );
}

export function generateSignedURLForS3Files(paths: string[]) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({ paths })
  };

  return APIFetchStudio<{ path_to_urls_map: Record<string, string> }>(
    `generate-signed-url/`,
    requestOptions
  );
}

// CF Workers services
export function saveMetaJSONChangesToCF(data: UpdateMetaJSONAndLogRequestData) {
  const requestOptions: RequestInit = {
    method: "PUT",
    headers: RequestHeaders.PUT,
    credentials: "include",
    body: JSON.stringify(data)
  };

  return APIFetchStudioActivityLogs(`update-meta-json/`, requestOptions);
}

export const getActivityLogs = ({
  appId,
  branchId,
  processId,
  branchSavepointId,
  processSavepointId,
  limit = 20,
  lastLogKey
}: {
  appId: number;
  branchId: number;
  branchSavepointId: number;
  processId: number;
  processSavepointId: number;
  limit?: number;
  lastLogKey?: string;
}) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      appId,
      branchId,
      branchSavepointId,
      processId,
      processSavepointId,
      limit,
      lastLogKey
    })
  };

  return APIFetchStudioActivityLogs<{
    logs: ActivityLogData[];
    cursor: string;
    hasMore: boolean;
  }>(`activity-logs`, requestOptions);
};

export function updateMegaJSON({
  branchSavepointId,
  payload
}: {
  branchSavepointId: number;
  payload: UpdateMegaJSONParams;
}): Promise<UpdateMegaJSONResponse | CreateMegaJSONResponse> {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({ data: payload })
  };

  return APIFetchStudio<UpdateMegaJSONResponse | CreateMegaJSONResponse>(
    `branch-savepoint/${branchSavepointId}/update-mega-json/`,
    requestOptions
  );
}

export function getMegaJSON({
  branchSavepointId,
  path
}: {
  branchSavepointId: number;
  path?: string;
}): Promise<GetMegaJSONResponse> {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: path ? JSON.stringify({ path }) : "{}"
  };

  return APIFetchStudio<GetMegaJSONResponse>(
    `branch-savepoint/${branchSavepointId}/get-mega-json/`,
    requestOptions
  );
}

export function getBranchByName({
  packageId,
  branchName
}: {
  packageId: number;
  branchName: string;
}): Promise<StudioBranch[]> {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<PaginatedResultsResponse<StudioBranchResponse>>(
    `branch/?package=${packageId}&name__iexact=${branchName}&limit=1`,
    requestOptions
  )
    .then(studioBranchesModelCreator)
    .then(response => response.results);
}

export function getLatestBranchSavepoint(
  branchId: number
): Promise<StudioBranchSavePoint | null> {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<
    PaginatedResultsResponse<StudioBranchSavePointResponse>
  >(`branch-savepoint/?branch=${branchId}&limit=1`, requestOptions)
    .then(studioBranchSavePointsModelCreator)
    .then(response => {
      return response.results.length > 0 ? response.results[0] : null;
    });
}

export function ecaDryRun(payload: {
  def_json: ZodRawProcess;
  rule_name: string;
  initial_state: Record<string, any>;
}) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };
  return APIFetchV3<ECARunResponse>(`eca-rule-run/`, requestOptions);
}
